import type { DayForecast, HuntingOutlook } from '../pages/DashboardPage';
import { timeToMinutes } from './forecastTime';

export interface HuntWindow {
  forecast: DayForecast;
  name: 'morning' | 'evening';
}

export function locationClock(outlook: HuntingOutlook, now: Date): { date: string; minutes: number } {
  if (outlook.timezone) {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: outlook.timezone, year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
    }).formatToParts(now);
    const part = (type: Intl.DateTimeFormatPartTypes) => {
      const value = parts.find(value => value.type === type);
      if (!value) throw new Error('Unable to determine the hunting location time');
      return value.value;
    };
    return {
      date: `${part('year')}-${part('month')}-${part('day')}`,
      minutes: Number(part('hour')) * 60 + Number(part('minute')),
    };
  }
  if (typeof outlook.utcOffsetSeconds !== 'number' || !Number.isFinite(outlook.utcOffsetSeconds)) {
    throw new Error('Forecast response is missing the hunting location timezone');
  }
  const local = new Date(now.getTime() + outlook.utcOffsetSeconds * 1000);
  return { date: local.toISOString().slice(0, 10), minutes: local.getUTCHours() * 60 + local.getUTCMinutes() };
}

export function selectHomeWindows(outlook: HuntingOutlook, now: Date): HuntWindow[] {
  const clock = locationClock(outlook, now);
  const today = outlook.forecasts.find(day => day.date === clock.date);
  if (!today || 'locked' in today) throw new Error('Forecast response is missing the current day');
  const afterSunset = clock.minutes >= timeToMinutes(today.sunset);
  const windows: HuntWindow[] = [];
  for (const forecast of outlook.forecasts) {
    if ('locked' in forecast || forecast.date < clock.date) continue;
    for (const name of ['morning', 'evening'] as const) {
      if (forecast.date === clock.date && (afterSunset || (name === 'morning' && clock.minutes >= 720))) continue;
      windows.push({ forecast, name });
      if (windows.length === 2) return windows;
    }
  }
  throw new Error('Forecast response is missing the next hunting windows');
}
